import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { Box, CircularProgress, Link as MuiLink, Stack, Typography } from "@mui/material";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import toast from "react-hot-toast";
import { useToken } from "../../../../server/auth/use-token";
import { ImagesService } from "../../../../server/cloudflare/images-service";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { toSlug } from "../../../../utils/create-slug";
import { NameSpaceURL, uuidv4, uuidv5 } from "../../../../utils/uuid";
import { useCreateAssetCallback } from "../../../assets/hooks/use-upsert-asset";
import { SupabaseDigitalAssetService } from "../../../assets/server/digital-asset-service";

export const QRCode: FC<{ title: string; link: string }> = ({ title, link }) => {
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const svgWrapperRef = useRef<HTMLDivElement>(null);

  const downloadPNG = useCallback(() => {
    const canvas = canvasWrapperRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error("Unable to download PNG");
      return;
    }
    const url = canvas.toDataURL("image/png");
    const aLink = document.createElement("a");
    aLink.href = url;
    aLink.download = `qr-code-${toSlug(title)}.png`;
    document.body.appendChild(aLink);
    aLink.click();
    document.body.removeChild(aLink);
  }, [title]);

  const generateSVGBlob = useCallback((): Blob | null => {
    const svg = svgWrapperRef.current?.querySelector("svg");
    if (!svg) return null;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    return new Blob([source], { type: "image/svg+xml;charset=utf-8" });
  }, []);

  const downloadSVG = useCallback(() => {
    const blob = generateSVGBlob();
    if (!blob) {
      toast.error("Unable to download SVG");
      return;
    }
    const url = URL.createObjectURL(blob);
    const aLink = document.createElement("a");
    aLink.href = url;
    aLink.download = `qr-code-${toSlug(title)}.svg`;
    document.body.appendChild(aLink);
    aLink.click();
    document.body.removeChild(aLink);
    URL.revokeObjectURL(url);
  }, [title, generateSVGBlob]);

  const getToken = useToken();
  const createAsset = useCreateAssetCallback();
  const [saving, setSaving] = useState(false);

  const expectedTitle = useMemo(() => `qr-code-${toSlug(title)}.svg`, [title]);
  const { data: existingAsset, reload: reloadExistingAsset } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data } = await new SupabaseDigitalAssetService(supabase).getAll(account_id, {
        order: [{ column: "updated_at", dir: "desc" }],
        filter: [
          { key: "title", operator: "eq", value: expectedTitle },
          { key: "deleted_at", operator: "is", value: null },
        ],
        limit: 1,
      });
      return data?.[0] ?? null;
    },
    [expectedTitle],
  );
  const saved = existingAsset !== null;

  const saveToContentLibrary = useCallback(async () => {
    const blob = generateSVGBlob();
    if (!blob) {
      toast.error("Unable to generate SVG");
      return;
    }

    setSaving(true);
    try {
      const fileName = `qr-code-${toSlug(title)}.svg`;
      const file = new File([blob], fileName, { type: "image/svg+xml" });
      const name = uuidv5(NameSpaceURL, uuidv4());
      const token = await getToken();

      const uploadResult = await new ImagesService().upload(file, name, token, file.type);
      if (!uploadResult.success) {
        toast.error("Failed to upload QR code");
        return;
      }

      const { success, error } = await createAsset({
        title: fileName,
        publicUrl: uploadResult.url,
        thumbnailUrl: uploadResult.url,
        contentType: "Uploaded File",
        mimeType: "image/svg+xml",
      });

      if (!success) {
        toast.error(error?.message ?? "Failed to save QR code to Content Library");
        return;
      }

      toast.success("Saved to your Content Library");
      reloadExistingAsset();
    } finally {
      setSaving(false);
    }
  }, [generateSVGBlob, title, getToken, createAsset, reloadExistingAsset]);

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-around"
        alignItems="center"
        width="100%"
      >
        {/* hidden canvas for PNG */}
        <Box ref={canvasWrapperRef} sx={{ display: "none" }}>
          <QRCodeCanvas value={link} size={256} />
        </Box>

        <Box ref={svgWrapperRef} sx={{ p: 1, bgcolor: "background.default" }}>
          <QRCodeSVG value={link} size={180} />
        </Box>

        <Stack spacing={3}>
          <MuiLink
            component="button"
            onClick={downloadPNG}
            underline="hover"
            sx={{ textAlign: "left" }}
          >
            <Typography variant="body2" fontWeight={500}>
              Download PNG
            </Typography>
          </MuiLink>
          <MuiLink
            component="button"
            onClick={downloadSVG}
            underline="hover"
            sx={{ textAlign: "left" }}
          >
            <Typography variant="body2" fontWeight={500}>
              Download SVG
            </Typography>
          </MuiLink>
          <MuiLink
            component="button"
            onClick={() => void saveToContentLibrary()}
            underline="hover"
            sx={{
              textAlign: "left",
              opacity: saving || saved ? 0.5 : 1,
              pointerEvents: saving || saved ? "none" : "auto",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" fontWeight={500}>
                Save to Content Library
              </Typography>
              {saving ? (
                <CircularProgress size={14} />
              ) : (
                saved && <CheckCircleOutline sx={{ fontSize: 16 }} color="success" />
              )}
            </Stack>
          </MuiLink>
        </Stack>
      </Stack>
    </Box>
  );
};
